import { CircularProgress } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';

import { getCompanyJobs, updateJob } from '../../../../services/jobs';

import CompanyHeader from './components/CompanyHeader';
import RolesTab from './components/RolesTab';
import Subscription from './components/Subscription';

import type { Company } from '../../../../types/company';
import type { SimpleInterview } from '../../../../types/interview';
import type { Job } from '../../../../types/job';
import type { User } from '../../../../types/user';

type JobsByStatus = {
    active: Job[];
    pending: Job[];
    closed: Job[];
};

const getOpenUpcomingInterviews = (interviews: SimpleInterview[]) =>
    interviews.filter(
        (interview) =>
            ['available', 'scheduled'].includes(interview.status) &&
            interview.startTime > DateTime.now()
    );

const groupByStatus = (jobs: Job[]) => {
    const jobsByStatus = {
        active: [],
        pending: [],
        closed: []
    } as JobsByStatus;
    if (jobs.length === 0) return jobsByStatus;
    jobs.forEach((job) => {
        const upcomingInterviews = getOpenUpcomingInterviews(job.interviews);
        if (!job.open) {
            jobsByStatus.closed.push(job);
        } else if (upcomingInterviews.length > 0) {
            jobsByStatus.active.push(job);
        } else {
            jobsByStatus.pending.push(job);
        }
    });
    return jobsByStatus;
};

const LoadingWheel = () => (
    <div
        style={{
            display: 'flex',
            justifyContent: 'center'
        }}
    >
        <CircularProgress />
    </div>
);

export default function EmployerJobList({
    user,
    company
}: {
    user: User;
    company: Company;
}) {
    const [jobs, setJobs] = useState<Job[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTab, setSelectedTab] = useState<
        'active' | 'pending' | 'completed'
    >('active');
    const [showJobs, setShowJobs] = useState({
        active: { expanded: false, jobs: 3 },
        pending: { expanded: false, jobs: 2 },
        completed: { expanded: false, jobs: 1 }
    });

    const subRoleCount = company.subscription?.activeRoles || 0;

    const jobSections = useMemo(() => {
        const { active, pending, closed } = groupByStatus(jobs);

        return {
            active: {
                title: 'Active Roles',
                tooltip:
                    'Active roles count against your subscription and represent roles that currently have upcoming interviews.',
                jobs: active
            },
            pending: {
                title: 'Pending Decision',
                tooltip:
                    'Roles that no longer have any active upcoming interviews but have not been filled or closed.',
                jobs: pending
            },
            completed: {
                title: 'Completed',
                tooltip:
                    'Roles that have been filled or are no longer open for applications.',
                jobs: closed
            }
        };
    }, [jobs]);

    const setJobOpen = (jobID: number, open: boolean) => {
        setJobs(
            jobs.map((job) => {
                if (job.id === jobID) {
                    updateJob({ ...job, open });
                    return { ...job, open };
                }
                return job;
            })
        );
    };

    useEffect(() => {
        if (company.id) {
            setIsLoading(true);
            getCompanyJobs(company.id)
                .then((response: Job[]) => {
                    setJobs(response);
                    setIsLoading(false);
                })
                .catch((err: any) => {
                    // eslint-disable-next-line no-console
                    console.log(err);
                    setIsLoading(false);
                });
        }
    }, [company.id, user.id, jobs.length]);

    useEffect(() => {
        (
            [
                'active',
                'pending',
                'completed'
            ] as unknown as (keyof typeof showJobs)[]
        ).forEach((section) => {
            setShowJobs((prev) => ({
                ...prev,
                [section]: prev[section].expanded
                    ? {
                          jobs: jobSections[section].jobs.length,
                          expanded: true
                      }
                    : {
                          jobs: prev[section].jobs,
                          expanded: false
                      }
            }));
        });
    }, [jobSections]);

    const handleTabChange = (
        event: React.SyntheticEvent,
        newValue: 'active' | 'pending' | 'completed'
    ) => {
        setSelectedTab(newValue);
    };

    if (isLoading) return <LoadingWheel />;

    return (
        <>
            <CompanyHeader
                company={company}
                jobSections={jobSections}
                user={user}
                subRoleCount={subRoleCount}
            />
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    marginTop: '1rem',
                    marginBottom: '1rem'
                }}
            >
                <Subscription
                    company={company}
                    jobs={jobs}
                    jobSections={jobSections}
                    user={user}
                    subRoleCount={subRoleCount}
                />
                <RolesTab
                    company={company}
                    jobSections={jobSections}
                    user={user}
                    subRoleCount={subRoleCount}
                    selectedTab={selectedTab}
                    handleTabChange={handleTabChange}
                    setJobOpen={setJobOpen}
                    showJobs={showJobs}
                />
            </div>
        </>
    );
}
